// src/pages/JewelleryPlans/Redemptions/RedemptionDetails.jsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getRedemptionByIdThunk,
  approveRedemptionThunk,
  cancelRedemptionThunk,
  rejectRedemptionThunk,
  updatePaymentThunk,
  completeRedemptionThunk,
} from "../../../store/thunks/jewelleryPlan/jewelleryThunks";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  ScaleIcon,
  DocumentTextIcon,
  PrinterIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
  BanIcon,
  RefreshIcon,
  SparklesIcon,
  InformationCircleIcon,
  CashIcon,
} from "@heroicons/react/outline";
import Button from "components/common/Button";

const RedemptionDetails = () => {
  const { redemptionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedRedemption, loading, error } = useSelector(
    (state) => state.jewelleryPlan?.redemption || {}
  );

  const [actionLoading, setActionLoading] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [approvalNote, setApprovalNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [paymentDetails, setPaymentDetails] = useState({
    method: "CASH",
    reference: "",
    transactionId: "",
    paidAmount: 0,
  });

  useEffect(() => {
    if (redemptionId) {
      dispatch(getRedemptionByIdThunk(redemptionId));
    }
  }, [redemptionId, dispatch]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { color: "yellow", icon: ClockIcon, text: "Pending" },
      PROCESSING: { color: "blue", icon: RefreshIcon, text: "Processing" },
      COMPLETED: { color: "green", icon: CheckCircleIcon, text: "Completed" },
      CANCELLED: { color: "red", icon: XCircleIcon, text: "Cancelled" },
    };
    return badges[status] || badges.PENDING;
  };

  const getApprovalStatusBadge = (status) => {
    const badges = {
      PENDING: { color: "yellow", icon: ClockIcon, text: "Awaiting Approval" },
      APPROVED: { color: "green", icon: ShieldCheckIcon, text: "Approved" },
      REJECTED: { color: "red", icon: BanIcon, text: "Rejected" },
    };
    return badges[status] || badges.PENDING;
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await dispatch(
        approveRedemptionThunk({
          redemptionId,
          approvalData: { note: approvalNote },
        })
      ).unwrap();
      setShowApprovalModal(false);
      setApprovalNote("");
    } catch (error) {
      console.error("Failed to approve:", error);
      alert(`Failed to approve redemption: ${error}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    try {
      setActionLoading(true);
      await dispatch(
        rejectRedemptionThunk({
          redemptionId,
          reason: rejectReason,
        })
      ).unwrap();
      setShowRejectModal(false);
      setRejectReason("");
    } catch (error) {
      console.error("Failed to reject:", error);
      alert(`Failed to reject redemption: ${error}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      alert("Please provide a cancellation reason");
      return;
    }

    try {
      setActionLoading(true);
      await dispatch(
        cancelRedemptionThunk({
          redemptionId,
          cancellationData: { reason: cancelReason },
        })
      ).unwrap();
      setShowCancelModal(false);
      setCancelReason("");
    } catch (error) {
      console.error("Failed to cancel:", error);
      alert(`Failed to cancel redemption: ${error}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!paymentDetails.method || paymentDetails.method === "PENDING") {
      alert("Please select a payment method");
      return;
    }

    try {
      setActionLoading(true);
      await dispatch(
        updatePaymentThunk({
          redemptionId,
          paymentDetails: {
            ...paymentDetails,
            paidAt: new Date(),
          },
        })
      ).unwrap();
      setShowPaymentModal(false);
      alert("Payment confirmed successfully");
    } catch (error) {
      console.error("Failed to confirm payment:", error);
      alert(`Failed to confirm payment: ${error}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    const confirm = window.confirm(
      "Are you sure you want to complete this redemption? This action cannot be undone."
    );
    if (!confirm) return;

    try {
      setActionLoading(true);
      await dispatch(
        completeRedemptionThunk({
          redemptionId,
          completionData: {},
        })
      ).unwrap();
      alert("Redemption completed successfully");
    } catch (error) {
      console.error("Failed to complete:", error);
      alert(`Failed to complete redemption: ${error}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-red-600 hover:text-red-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!selectedRedemption) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Redemption not found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-yellow-600 hover:text-yellow-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const redemption = selectedRedemption;
  const statusBadge = getStatusBadge(redemption.status);
  const StatusIcon = statusBadge.icon;
  const isValueRedemption = redemption.redemptionType === "VALUE_REDEMPTION";
  const isValuePlan = redemption.planType === "VALUE_SAVINGS";

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 mb-4 flex items-center"
        >
          ← Back
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Redemption #{redemption.redemptionNumber}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Created on {formatDate(redemption.createdAt)}
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${statusBadge.color}-100 text-${statusBadge.color}-800`}
              >
                <StatusIcon className="h-4 w-4 mr-1" />
                {statusBadge.text}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {isValueRedemption ? "Value Redemption" : "Item Redemption"}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {redemption.planType?.replace(/_/g, " ")}
              </span>
            </div>
          </div>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Print
          </button>
        </div>
      </div>

      {/* Approval Alert */}
      {redemption.requiresApproval && (
        <div
          className={`mb-6 rounded-lg p-4 ${
            redemption.approvalStatus === "PENDING"
              ? "bg-yellow-50 border border-yellow-200"
              : redemption.approvalStatus === "APPROVED"
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {(() => {
                const badge = getApprovalStatusBadge(redemption.approvalStatus);
                const ApprovalIcon = badge.icon;
                return (
                  <>
                    <ApprovalIcon
                      className={`h-6 w-6 text-${badge.color}-600 mr-3`}
                    />
                    <div>
                      <p
                        className={`text-sm font-medium text-${badge.color}-900`}
                      >
                        {badge.text}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Reason:{" "}
                        {redemption.approvalReason ||
                          "High value or policy requirement"}
                      </p>
                      {redemption.approvalNote && (
                        <p className="text-xs text-gray-600 mt-1">
                          Note: {redemption.approvalNote}
                        </p>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            {redemption.approvalStatus === "PENDING" &&
              redemption.status !== "CANCELLED" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="px-3 py-1.5 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm font-medium"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setShowApprovalModal(true)}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                  >
                    Approve
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                {isValuePlan ? (
                  <CurrencyRupeeIcon className="h-8 w-8 text-green-600" />
                ) : (
                  <ScaleIcon className="h-8 w-8 text-green-600" />
                )}
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {isValuePlan
                  ? formatAmount(
                      redemption.summary?.redeemedValue ||
                        redemption.summary?.walletValueUsed ||
                        0
                    )
                  : `${
                      redemption.summary?.redeemedGrams ||
                      redemption.summary?.walletGramsUsed ||
                      0
                    }g`}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {isValuePlan ? "Redeemed Value" : "Redeemed Gold"}
              </p>
            </div>

            {redemption.summary?.benefitsApplied && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <SparklesIcon className="h-8 w-8 text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-gray-900">
                  {isValuePlan
                    ? `${redemption.summary.benefitWaiverPercent || 0}%`
                    : `+${redemption.summary.benefitGrams || 0}g`}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {isValuePlan ? "Making Charge Waiver" : "Bonus Grams"}
                </p>
              </div>
            )}
          </div>

          {/* Financial Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Financial Summary
            </h2>

            {isValueRedemption ? (
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Redeemed Value:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(
                      redemption.summary?.redeemedValue ||
                        redemption.summary?.walletValueUsed ||
                        0
                    )}
                  </span>
                </div>
                {!isValuePlan && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Redeemed Grams:
                    </span>
                    <span className="text-sm font-medium">
                      {redemption.summary?.redeemedGrams ||
                        redemption.summary?.walletGramsUsed ||
                        0}
                      g
                    </span>
                  </div>
                )}
                {(redemption.summary?.benefitGrams || 0) > 0 && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Bonus Grams Included:
                    </span>
                    <span className="text-sm font-medium text-green-600">
                      +{redemption.summary.benefitGrams}g
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-2 bg-gray-50 rounded px-2">
                  <span className="text-base font-medium">Total Value:</span>
                  <span className="text-base font-bold">
                    {formatAmount(redemption.summary?.totalValue || 0)}
                  </span>
                </div>

                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
                    <p className="text-xs text-blue-800">
                      This is a value redemption. The customer has redeemed
                      their plan balance as store credit. They can use this
                      credit when selecting jewelry items at the store.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Gold Value:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(redemption.summary?.totalGoldValue || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Making Charges:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(redemption.summary?.totalMakingCharges || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">VA Charges:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(redemption.summary?.totalVACharges || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Subtotal:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(redemption.summary?.subtotal || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b text-green-600">
                  <span className="text-sm">Wallet Applied:</span>
                  <span className="text-sm font-medium">
                    -{" "}
                    {formatAmount(
                      redemption.summary?.walletValueUsed ||
                        (redemption.summary?.walletGramsUsed || 0) *
                          (redemption.rateSnapshot?.ratePerGram || 0)
                    )}
                  </span>
                </div>
                {(redemption.summary?.benefitAmount || 0) > 0 && (
                  <div className="flex justify-between py-2 border-b text-purple-600">
                    <span className="text-sm">Benefits Applied:</span>
                    <span className="text-sm font-medium">
                      - {formatAmount(redemption.summary.benefitAmount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">GST:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(redemption.summary?.gst || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 bg-gray-50 rounded px-2">
                  <span className="text-base font-medium">Total:</span>
                  <span className="text-base font-bold">
                    {formatAmount(redemption.summary?.totalValue || 0)}
                  </span>
                </div>
                {(redemption.summary?.additionalPayment || 0) > 0 && (
                  <div className="flex justify-between py-2 bg-red-50 rounded px-2 border border-red-200">
                    <span className="text-base font-medium text-red-700">
                      Additional Payment:
                    </span>
                    <span className="text-base font-bold text-red-700">
                      {formatAmount(redemption.summary.additionalPayment)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Items */}
          {!isValueRedemption && redemption.items?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Items ({redemption.items.length})
              </h2>
              <div className="space-y-3">
                {redemption.items.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">
                          {item.category} • {item.goldWeightGrams}g •{" "}
                          {item.karat}K
                        </p>
                        {item.skuCode && (
                          <p className="text-xs text-gray-400">
                            SKU: {item.skuCode}
                          </p>
                        )}
                      </div>
                      <p className="font-bold text-gray-900">
                        {formatAmount(item.calculatedValues?.itemTotal || 0)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Details */}
          {/* <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Payment Details
              </h2>
              {redemption.additionalPaymentDetails?.method === "PENDING" &&
                redemption.status === "PENDING" && (
                  <button
                    onClick={() => {
                      setPaymentDetails((p) => ({
                        ...p,
                        paidAmount: redemption.summary?.additionalPayment || 0,
                      }));
                      setShowPaymentModal(true);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Update Payment
                  </button>
                )}
            </div>

            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Payment Method:</span>
                <span
                  className={`text-sm font-medium ${
                    redemption.additionalPaymentDetails?.method === "PENDING"
                      ? "text-yellow-600"
                      : redemption.additionalPaymentDetails?.method ===
                        "NOT_APPLICABLE"
                      ? "text-gray-500"
                      : "text-green-600"
                  }`}
                >
                  {redemption.additionalPaymentDetails?.method || "PENDING"}
                </span>
              </div>
              {redemption.additionalPaymentDetails?.reference && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Reference:</span>
                  <span className="text-sm font-medium">
                    {redemption.additionalPaymentDetails.reference}
                  </span>
                </div>
              )}
              {redemption.additionalPaymentDetails?.transactionId && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Transaction ID:</span>
                  <span className="text-sm font-medium">
                    {redemption.additionalPaymentDetails.transactionId}
                  </span>
                </div>
              )}
              {redemption.additionalPaymentDetails?.paidAmount > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Amount Paid:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(
                      redemption.additionalPaymentDetails.paidAmount
                    )}
                  </span>
                </div>
              )}
              {redemption.additionalPaymentDetails?.paidAt && (
                <div className="flex justify-between py-2">
                  <span className="text-sm text-gray-600">Paid At:</span>
                  <span className="text-sm font-medium">
                    {formatDate(redemption.additionalPaymentDetails.paidAt)}
                  </span>
                </div>
              )}
            </div>
          </div> */}

          {/* Rate Information */}
          {redemption.rateSnapshot && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Rate Information
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Rate Strategy:</span>
                  <span className="text-sm font-medium">
                    {redemption.rateStrategy?.replace(/_/g, " ") || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Rate Per Gram:</span>
                  <span className="text-sm font-medium">
                    {formatAmount(redemption.rateSnapshot?.ratePerGram || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Karat:</span>
                  <span className="text-sm font-medium">
                    {redemption.rateSnapshot?.karat || 22}K
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-sm text-gray-600">Rate Date:</span>
                  <span className="text-sm font-medium">
                    {formatDate(redemption.rateSnapshot?.rateDate)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h2>
            <div className="space-y-3">
              {redemption.status === "PENDING" && (
                <>
                  {redemption.requiresApproval &&
                  redemption.approvalStatus === "PENDING" ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-yellow-800">
                        ⏳ Waiting for approval before proceeding
                      </p>
                    </div>
                  ) : redemption.approvalStatus === "REJECTED" ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-red-800">
                        ❌ Approval was rejected. Consider cancelling this
                        redemption.
                      </p>
                    </div>
                  ) : (
                    <>
                      {(redemption.summary?.additionalPayment || 0) > 0 &&
                      redemption.additionalPaymentDetails?.method ===
                        "PENDING" ? (
                        <button
                          onClick={() => {
                            setPaymentDetails((p) => ({
                              ...p,
                              paidAmount:
                                redemption.summary?.additionalPayment || 0,
                            }));
                            setShowPaymentModal(true);
                          }}
                          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center font-medium"
                        >
                          <CashIcon className="h-5 w-5 mr-2" />
                          Confirm Payment
                        </button>
                      ) : (
                        <>
                          <Button
                            className="w-full"
                            onClick={() => {
                              const url = `/consultation/start-session?userId=${redemption.userId._id}`;
                              window.open(url, "_blank");
                            }}
                          >
                            Create Order
                          </Button>
                          <button
                            onClick={handleComplete}
                            disabled={actionLoading}
                            className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center font-medium disabled:opacity-50"
                          >
                            {actionLoading ? (
                              <>
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                  />
                                </svg>
                                Processing...
                              </>
                            ) : (
                              <>
                                <CheckCircleIcon className="h-5 w-5 mr-2" />
                                Complete Redemption
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </>
                  )}

                  <button
                    onClick={() => setShowCancelModal(true)}
                    disabled={actionLoading}
                    className="w-full px-4 py-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 flex items-center justify-center font-medium disabled:opacity-50"
                  >
                    <XCircleIcon className="h-5 w-5 mr-2" />
                    Cancel Redemption
                  </button>
                </>
              )}

              {redemption.status === "COMPLETED" && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-900">
                        Redemption Completed
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        {formatDate(redemption.completedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {redemption.status === "CANCELLED" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <XCircleIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-900">
                        Redemption Cancelled
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        Reason:{" "}
                        {redemption.cancellationReason || "Not specified"}
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        {formatDate(redemption.cancelledAt)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => navigate(`/jewellery/${redemption.planId}`)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                View Plan Details
              </button>
            </div>
          </div>

          {/* User Details - Add this in the sidebar section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Customer Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Name:</span>
                <span className="text-sm font-medium">
                  {redemption.userId?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Email:</span>
                <span className="text-sm font-medium">
                  {redemption.userId?.email || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Phone:</span>
                <span className="text-sm font-medium">
                  {redemption.userId?.phoneNumber || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Redemption Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Redemption Info
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Redemption #:</span>
                <span className="font-medium">
                  {redemption.redemptionNumber}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Plan Type:</span>
                <span className="font-medium">
                  {redemption.planType?.replace(/_/g, " ")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Redemption Type:</span>
                <span className="font-medium">
                  {redemption.redemptionType?.replace(/_/g, " ")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Branch:</span>
                <span className="font-medium">{redemption.branchCode}</span>
              </div>
              {redemption.invoiceNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice #:</span>
                  <span className="font-medium">
                    {redemption.invoiceNumber}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Quote Reference */}
          {redemption.quoteSnapshot && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Quote Reference
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Quote #:</span>
                  <span className="font-medium">
                    {redemption.quoteSnapshot.quoteNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Quote Expired:</span>
                  <span className="font-medium text-xs">
                    {formatDate(redemption.quoteSnapshot.expiresAt)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {redemption.notes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Notes</h3>
              <p className="text-sm text-gray-700">{redemption.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Approve Redemption</h3>
            <textarea
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
              placeholder="Add approval note (optional)"
              className="w-full border rounded-lg px-3 py-2 mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? "Approving..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Redemption</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Rejection reason (required)"
              className="w-full border rounded-lg px-3 py-2 mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Cancel Redemption</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will reverse the redemption and restore the plan wallet.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (required)"
              className="w-full border rounded-lg px-3 py-2 mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Keep
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading || !cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? "Cancelling..." : "Cancel Redemption"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Payment</h3>
            <p className="text-sm text-gray-600 mb-4">
              Amount:{" "}
              <strong>
                {formatAmount(redemption.summary?.additionalPayment || 0)}
              </strong>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method *
                </label>
                <select
                  value={paymentDetails.method}
                  onChange={(e) =>
                    setPaymentDetails((p) => ({
                      ...p,
                      method: e.target.value,
                    }))
                  }
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="NEFT">NEFT</option>
                  <option value="RTGS">RTGS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={paymentDetails.reference}
                  onChange={(e) =>
                    setPaymentDetails((p) => ({
                      ...p,
                      reference: e.target.value,
                    }))
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Enter reference"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount Paid *
                </label>
                <input
                  type="number"
                  value={paymentDetails.paidAmount}
                  onChange={(e) =>
                    setPaymentDetails((p) => ({
                      ...p,
                      paidAmount: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? "Confirming..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedemptionDetails;
